import { Epic } from 'redux-observable'
import { filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import { portfolioApiTransformers } from '../portfolio'
import { multiUserOperators } from '../../multiUser/state'
import { ENDPOINTS } from './constants'
import { events, PropertyEvent } from './state'
import {
  TCombinedSearchResponse,
  TGooglePlace,
  TLightstoneProperty,
  TProperty,
  TPropertySearchResult,
  TPropertySummary,
} from './types'

type PropertyEpic = Epic<PropertyEvent, PropertyEvent, RootState, EpicDependencies>

/**
 * API Requests
 */

export const apiCreateProperty: PropertyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createProperty_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.BASE, state$, action.payload, {}).pipe(
        pluck('response'),
        map((res: TProperty) => events.createProperty_success(res)),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiCreateProperty'),
  )

export const apiUpdateProperty: PropertyEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateProperty_request.type)),
    mergeMap(action => {
      const { id, values } = action.payload
      return put(ENDPOINTS.UPDATE, state$, values, { id }).pipe(
        pluck('response'),
        mergeMap((response: TProperty): PropertyEvent[] => [
          events.updateProperty_success(response),
          events.properties_request(),
        ]),
        catchRestError(action),
      )
    }),
    tag('property/epics/apiUpdateProperty'),
  )

export const apiFetchProperties: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.properties_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.BASE, state$, {}).pipe(
        pluck('response'),
        map((res: TPropertySummary[]) => events.properties_success(res)),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiFetchProperties'),
  )

export const apiSearchProperties: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.search_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.SEARCH, state$, action.payload).pipe(
        pluck('response'),
        map((response: { limit: number; offset: number; properties: TPropertySearchResult[] }) =>
          events.search_success({
            query: action.payload.query,
            response: response.properties,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiSearchProperties'),
  )

export const apiCombinedSearchProperties: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.combinedSearch_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.COMBINED_SEARCH, state$, action.payload).pipe(
        pluck('response'),
        map((response: TCombinedSearchResponse) =>
          events.combinedSearch_success({
            query: action.payload.query,
            response,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiCombinedSearchProperties'),
  )

export const apiFetchPlace: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.place_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.PLACE, state$, action.payload).pipe(
        pluck('response'),
        map((response: TGooglePlace) => events.place_success({ response, placeId: action.payload.placeId })),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiFetchPlace'),
  )

export const apiFetchProperty: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.property_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.ENTITY, state$, action.payload).pipe(
        pluck('response'),
        map((res: TProperty) => events.property_success(res)),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiFetchProperty'),
  )

export const apiFetchLightstoneProperty: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.lightstoneProperty_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.LIGHTSTONE_ENTITY, state$, action.payload).pipe(
        pluck('response'),
        map((res: TLightstoneProperty) => events.lightstoneProperty_success(res)),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiFetchLightstoneProperty'),
  )

/**
 * @todo prefetch portfolios?
 */
export const apiFetchPropertyPortfolios: PropertyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.propertyPortfolios_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.PORTFOLIOS, state$, action.payload).pipe(
        pluck('response'),
        map((response: unknown[]) =>
          events.propertyPortfolios_success({
            response: portfolioApiTransformers.transformGetPortfolioResponse(response),
            propertyId: action.payload.id,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('property/epics/apiFetchPropertyPortfolios'),
  )
